import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import HotelPackagesModal from "@/components/HotelPackagesModal";
import Footer from "@/components/Footer";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import { PHOTOS } from "@/lib/photos";
import { useMobileOnceInView, useMobileOnceViewport } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";

const images = {
  hero1: PHOTOS.VENUE.roostertailExteriorNight,
  hero2: PHOTOS.BOAT.groupSixPeopleWaving,
  content1: PHOTOS.GROUP.fiveFriendsRoostertail,
  content2: PHOTOS.GROUP.eightPeopleGazebo,
};

const Travel = () => {
  usePageSEO({
    title: "Travel and Hotels — Yacht Rock Detroit 2026",
    description: "Directions, parking, hotel deals, and rideshare tips for getting to The Roostertail in Detroit.",
    keywords: "yacht rock detroit travel, roostertail directions, detroit hotel deals, parking roostertail",
    ogImage: PHOTOS.BOAT.groupSixPeopleWaving,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Travel", url: "https://yachtrockdetroit.com/travel" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });
  const mobileOnceViewport = useMobileOnceViewport("-100px");
  
  const [hotelModalOpen, setHotelModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />
      <HotelPackagesModal
        isOpen={hotelModalOpen} 
        onClose={() => setHotelModalOpen(false)}
      />

      {/* Hero Header Section */}
      <section 
        ref={headerRef}
        className="relative min-h-fit md:min-h-[80vh] bg-yacht-blue overflow-hidden flex flex-col md:flex-row md:items-end"
      >
        <div 
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />

        <img 
          src={boatsGraphic}
          alt=""
          className="absolute pointer-events-none z-0
            w-[800%] left-1/2 -translate-x-1/2 top-0
            sm:w-[150%] sm:-top-[30%]
            lg:w-[90%] lg:-left-[10%] lg:-top-[30%] lg:translate-x-0"
          style={{
            opacity: 0.15,
            filter: 'brightness(0) invert(1)',
          }}
        />

        {/* MOBILE LAYOUT */}
        <div className="md:hidden relative z-20 flex flex-col items-center pt-28 pb-8 px-6">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-3 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Getting There
          </motion.p>
          <div className="text-[6vw] font-bold text-white leading-[0.9] tracking-tight text-center mb-8" role="heading" aria-level={1}>
            <GradualSpacing text="Getting to Yacht Rock Detroit" duration={0.5} delayMultiple={0.03} />
            <br />
            <span className="text-[5vw]">
              <GradualSpacing text="Hotels, Parking & Directions" duration={0.5} delayMultiple={0.03} />
            </span>
          </div>
          
          <div className="relative w-full max-w-xs h-[320px]">
            <motion.div
              className="absolute left-0 top-0 w-[60%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-10"
              initial={{ opacity: 0, y: 30, rotate: -5 }}
              animate={isHeaderInView ? { opacity: 1, y: 0, rotate: -5 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <img src={images.hero1} alt="The Roostertail venue lit up at night on the Detroit River" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div
              className="absolute right-0 top-8 w-[60%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-20"
              initial={{ opacity: 0, y: 30, rotate: 5 }}
              animate={isHeaderInView ? { opacity: 1, y: 0, rotate: 5 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <img src={images.hero2} alt="Friends waving from a boat at Yacht Rock Detroit" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>

        {/* DESKTOP LAYOUT */}
        <motion.div
          className="hidden md:block absolute top-44 right-[5%] w-[35%] max-w-md aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl"
          initial={{ opacity: 0, y: 50, rotate: 3 }}
          animate={isHeaderInView ? { opacity: 1, y: 0, rotate: 3 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <img src={images.hero1} alt="The Roostertail venue lit up at night on the Detroit River" className="w-full h-full object-cover" />
        </motion.div>

        <motion.div
          className="hidden md:block absolute bottom-24 right-[25%] w-[25%] max-w-xs aspect-square rounded-2xl overflow-hidden shadow-2xl z-10"
          initial={{ opacity: 0, y: 30, rotate: -5 }}
          animate={isHeaderInView ? { opacity: 1, y: 0, rotate: -5 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <img src={images.hero2} alt="Friends waving from a boat at Yacht Rock Detroit" className="w-full h-full object-cover" />
        </motion.div>

        <div className="hidden md:block relative z-20 px-8 md:px-16 pb-16 md:pb-24">
          <motion.p
            className="text-yacht-yellow/80 text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Getting There
          </motion.p>
          <h1 className="text-[10vw] lg:text-[8vw] font-bold text-white leading-[0.85] tracking-tight">
            <GradualSpacing text="Travel" duration={0.5} delayMultiple={0.08} />
          </h1>
        </div>
      </section>

      {/* Yellow Accent Strip */}
      <section className="relative py-8 bg-yacht-yellow overflow-hidden">
        <div 
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 px-8 md:px-16">
          <p className="text-yacht-charcoal/80 text-lg md:text-xl font-medium text-center" style={{ fontFamily: 'Georgia, serif' }}>
            Hotels, parking, and getting to the waterfront.
          </p>
        </div>
      </section>

      {/* Location Section - Big Background Text Style */}
      <section className="relative bg-yacht-cream overflow-hidden py-8 md:py-16">
        {/* Big text behind - positioned so 80% is visible above map */}
        <div className="absolute inset-x-0 top-[4vw] md:top-[3vw] flex justify-center pointer-events-none select-none z-0">
          <motion.h2 
            className="text-[18vw] md:text-[14vw] font-bold text-yacht-blue/20 tracking-tight whitespace-nowrap"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={mobileOnceViewport}
          >
            LOCATION
          </motion.h2>
        </div>
        
        {/* Map on top - positioned to overlap bottom 20% of text */}
        <div className="relative z-10 px-4 md:px-8 pt-[18vw] md:pt-[14vw]">
          <motion.div
            className="w-full max-w-5xl mx-auto aspect-video rounded-2xl overflow-hidden shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={mobileOnceViewport}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2949.5!2d-82.9876!3d42.3456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8824d2c7a0f1234%3A0x1234567890abcdef!2s100%20Marquette%20Dr%2C%20Detroit%2C%20MI%2048214!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="The Roostertail Location"
            />
          </motion.div>
        </div>

        {/* Venue details below map */}
        <div className="relative z-10 max-w-5xl mx-auto px-8 mt-8 md:mt-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <p className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-2">
                The Venue
              </p>
              <h3 className="text-3xl md:text-4xl font-bold text-yacht-charcoal">
                The Roostertail
              </h3>
              <p className="text-yacht-charcoal/60 text-lg mt-2" style={{ fontFamily: 'Georgia, serif' }}>
                100 Marquette Drive, Detroit, MI 48214
              </p>
            </motion.div>
            
            <motion.a
              href="https://www.google.com/maps/place/100+Marquette+Dr,+Detroit,+MI+48214"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-yacht-blue text-white font-bold text-base rounded-full hover:scale-105 transition-transform shadow-lg w-fit"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Get Directions
            </motion.a>
          </div>
        </div>
        
        {/* Bottom border */}
        <div className="relative h-[20px] bg-yacht-blue mt-12 md:mt-16">
          <div 
            className="absolute inset-0 opacity-[0.15] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }}
          />
        </div>
      </section>

      {/* Hotels Section - Big Centered Title Style */}
      <section className="py-20 md:py-32 px-8 md:px-16 bg-yacht-cream overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Big Centered Title */}
          <div className="text-center mb-8 md:mb-12">
            <motion.h2 
              className="text-[14vw] md:text-[10vw] lg:text-[8vw] font-bold text-yacht-charcoal leading-[0.85] tracking-tight"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              HOTELS &
            </motion.h2>
            <motion.h2 
              className="text-[14vw] md:text-[10vw] lg:text-[8vw] font-bold text-yacht-blue leading-[0.85] tracking-tight italic"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              DEALS
            </motion.h2>
          </div>

          {/* Description */}
          <motion.p 
            className="text-yacht-charcoal/60 text-lg md:text-xl text-center max-w-2xl mx-auto mb-12 md:mb-16" 
            style={{ fontFamily: 'Georgia, serif' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            We're working with local hotels to bring you the best rates for your stay. 
            Hotel packages and discounts will be available at a further date.
          </motion.p>

          {/* Photos Grid with Coming Soon Card */}
          <div className="relative h-[450px] md:h-[550px] max-w-5xl mx-auto">
            {/* Photo 1 - Left, tilted */}
            <motion.div
              className="absolute left-0 md:left-[5%] top-0 w-[45%] md:w-[35%] aspect-[3/4] rounded-2xl overflow-hidden shadow-lg z-10"
              initial={{ opacity: 0, y: 50, rotate: -6 }}
              whileInView={{ opacity: 1, y: 0, rotate: -6 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <img 
                src={images.content1} 
                alt="Five friends posing at The Roostertail waterfront" 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
            
            {/* Coming Soon Card - Full width on mobile, centered on desktop */}
            <motion.div
              className="absolute left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:right-auto bottom-4 md:bottom-auto md:top-4 md:w-[40%] bg-yacht-blue rounded-2xl p-6 md:p-10 shadow-2xl z-20"
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <div 
                className="absolute inset-0 opacity-[0.08] pointer-events-none rounded-2xl"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                }}
              />
              <span className="inline-block text-yacht-yellow/60 text-xs md:text-sm font-medium tracking-[0.3em] uppercase mb-3 relative z-10">
                 Hotel Packages
              </span>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3 relative z-10">
                Stay Close
              </h3>
              <p className="text-white/70 text-sm md:text-base leading-relaxed relative z-10 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                The best weekend starts with the perfect place to crash.
              </p>
              <button
                onClick={() => setHotelModalOpen(true)}
                className="relative z-10 px-6 py-3 bg-yacht-yellow text-yacht-charcoal font-semibold rounded-full hover:scale-105 transition-transform text-sm"
              >
                Get Updates
              </button>
            </motion.div>
            
            {/* Photo 2 - Right, tilted */}
            <motion.div
              className="absolute right-0 md:right-[5%] top-0 w-[45%] md:w-[35%] aspect-[3/4] rounded-2xl overflow-hidden shadow-lg z-10"
              initial={{ opacity: 0, y: 50, rotate: 6 }}
              whileInView={{ opacity: 1, y: 0, rotate: 6 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <img 
                src={images.content2} 
                alt="Group gathering under the gazebo at The Roostertail" 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
            
            {/* Floating yellow accent */}
            <motion.div 
              className="absolute bottom-8 left-[15%] w-16 h-16 md:w-24 md:h-24 bg-yacht-yellow rounded-xl -z-0"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 0.7, scale: 1 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.6, delay: 0.7 }}
            />
          </div>
        </div>
      </section>

      {/* Parking Section - Big Background Text on Blue */}
      <section className="relative bg-yacht-blue overflow-hidden py-20 md:py-32">
        <div 
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        
        {/* Big text behind - positioned at top */}
        <div className="absolute inset-x-0 top-8 md:top-12 flex justify-center pointer-events-none select-none z-0">
          <motion.h2 
            className="text-[25vw] md:text-[18vw] font-bold text-white/10 tracking-tight whitespace-nowrap"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={mobileOnceViewport}
          >
            PARKING
          </motion.h2>
        </div>
        
        {/* Content card - pushed down to reveal text */}
        <div className="relative z-10 max-w-4xl mx-auto px-8 pt-16 md:pt-24">
          <motion.div
            className="bg-yacht-cream rounded-2xl p-10 md:p-16 shadow-xl"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <motion.p
                  className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={mobileOnceViewport}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  Getting There
                </motion.p>
                <h3 className="text-4xl md:text-5xl font-bold text-yacht-charcoal mb-6">
                  Parking Info
                </h3>
                <p className="text-yacht-charcoal/60 text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                  Subject to change, but very limited ultra VIP parking may be available on site. 
                  Other parking instructions will be announced in detail at a further date.
                </p>
              </div>
              
              <motion.div
                className="flex justify-center md:justify-end"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={mobileOnceViewport}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="w-32 h-32 md:w-40 md:h-40 bg-yacht-yellow rounded-full flex items-center justify-center">
                  <span className="text-yacht-charcoal font-bold text-lg md:text-xl text-center px-4">
                    Details Soon
                  </span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-yacht-yellow">
        <div 
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="max-w-4xl mx-auto text-center px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-yacht-charcoal mb-6">
            Questions?
          </h2>
          <p className="text-yacht-charcoal/70 text-xl mb-8" style={{ fontFamily: 'Georgia, serif' }}>
            Check out our FAQ or get in touch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/faq"
              className="inline-flex items-center justify-center px-8 py-4 bg-yacht-charcoal text-white font-semibold text-lg rounded-full hover:bg-yacht-charcoal/90 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              View FAQ
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-yacht-blue text-white font-semibold text-lg rounded-full hover:bg-yacht-blue/90 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Travel;
